import React, { useEffect, useRef, useState } from 'react';
import { aiChat } from './anomalyApi';

const s = {
    page:    { padding: 24, background: '#f8fafc', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif', display: 'flex', flexDirection: 'column' },
    h1:      { fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 },
    sub:     { fontSize: 13, color: '#64748b', marginTop: 4 },
    chatBox: { flex: 1, marginTop: 16, marginBottom: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' },
    msgUser: { background: '#0f172a', color: '#fff', borderRadius: 8, padding: '10px 14px', maxWidth: '72%', alignSelf: 'flex-end', fontSize: 14, lineHeight: 1.45 },
    msgBot:  { background: '#f1f5f9', color: '#0f172a', borderRadius: 8, padding: '10px 14px', maxWidth: '92%', alignSelf: 'flex-start', fontSize: 14, lineHeight: 1.5 },
    row:     { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 },
    inputRow:{ display: 'flex', gap: 8, alignItems: 'flex-end' },
    ta:      { flex: 1, minHeight: 60, padding: 12, fontSize: 14, border: '1px solid #cbd5e1', borderRadius: 8, fontFamily: 'inherit', resize: 'vertical' },
    btn:     { padding: '10px 20px', borderRadius: 8, background: '#0f172a', color: '#fff', fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' },
    sqlBox:  { marginTop: 10, padding: 10, background: '#0f172a', color: '#86efac', fontSize: 12, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', borderRadius: 6, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
    meta:    { marginTop: 6, fontSize: 11, color: '#64748b' },
    err:     { padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 },
    dates:   { display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8 },
    inDate:  { padding: 6, border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 },
    suggest: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
    chip:    { padding: '4px 10px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 999, fontSize: 12, color: '#334155', cursor: 'pointer' },
};

const SUGGESTIONS = [
    'Which 5 cardholders spent the most on fuel in the last 30 days?',
    'List open high-severity flags grouped by manager.',
    'Show me weekly fuel gallons for Job 3211 over the last 8 weeks.',
    'Which cost codes are over budget on jobs with active flags?',
    'How many flags did the engine raise per rule in the last 60 days?',
];

export default function AnomalyChat() {
    const todayStr = new Date().toISOString().slice(0, 10);
    const yearAgoStr = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0,10); })();
    const [startDate, setStartDate] = useState(yearAgoStr);
    const [endDate, setEndDate]   = useState(todayStr);
    const [input, setInput] = useState('');
    const [thread, setThread] = useState([]); // [{role,content,sql?,explanation?,trace?}]
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const scrollerRef = useRef(null);

    useEffect(() => {
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }, [thread]);

    async function send(text) {
        const q = (text != null ? text : input).trim();
        if (!q || busy) return;
        setError(null);
        setInput('');
        setThread(t => [...t, { role: 'user', content: q }]);
        setBusy(true);
        try {
            const r = await aiChat({ question: q, startDate, endDate });
            setThread(t => [...t, {
                role: 'bot',
                content: r.answer || (r.error ? `Error: ${r.error}` : '(no answer)'),
                sql: r.sql,
                explanation: r.explanation,
                rowCount: r.rowCount,
                totalMs: r.totalMs,
            }]);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div style={s.page}>
            <div>
                <h1 style={s.h1}>Anomaly Q&A</h1>
                <div style={s.sub}>Ask in plain English. The pipeline rewrites → plans → writes SQL → runs against the anomaly schema → explains.</div>
                <div style={s.dates}>
                    <span style={{ fontSize: 11, color: '#64748b' }}>WINDOW</span>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={s.inDate} />
                    <span style={{ color: '#94a3b8' }}>–</span>
                    <input type="date" value={endDate}   onChange={e => setEndDate(e.target.value)} style={s.inDate} />
                </div>
                {thread.length === 0 && (
                    <div style={s.suggest}>
                        {SUGGESTIONS.map(q => (
                            <span key={q} style={s.chip} onClick={() => send(q)}>{q}</span>
                        ))}
                    </div>
                )}
            </div>

            <div style={s.chatBox} ref={scrollerRef}>
                {thread.map((m, i) => (
                    <div key={i} style={s.row}>
                        <div style={m.role === 'user' ? s.msgUser : s.msgBot}>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
                            {m.role === 'bot' && m.sql && (
                                <>
                                    <div style={s.sqlBox}>{m.sql.trim()}</div>
                                    {m.explanation && <div style={s.meta}>{m.explanation}</div>}
                                    <div style={s.meta}>
                                        {m.rowCount != null ? `${m.rowCount} rows · ` : ''}
                                        {m.totalMs != null ? `${m.totalMs} ms` : ''}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ))}
                {busy && <div style={{ ...s.msgBot, opacity: .6 }}>Thinking…</div>}
            </div>

            {error && <div style={s.err}>Error: {error}</div>}

            <div style={s.inputRow}>
                <textarea
                    style={s.ta}
                    placeholder="Ask about spend, flags, fuel, timecards…"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); } }}
                />
                <button style={s.btn} onClick={() => send()} disabled={busy || !input.trim()}>
                    {busy ? 'Working…' : 'Send'}
                </button>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Ctrl/⌘ + Enter to send</div>
        </div>
    );
}
